import { Box, Typography, Button } from "@mui/material"
import { Refresh } from "@mui/icons-material"
import { enqueueSnackbar } from "notistack"
import React from "react"
import { LOADING_HEIGHT } from "./constants"

interface DashboardErrorBoundaryProps {
  children: React.ReactNode
}

interface DashboardErrorBoundaryState {
  hasError: boolean
  error?: Error
  errorId: string
}

class DashboardErrorBoundary extends React.Component<
  DashboardErrorBoundaryProps,
  DashboardErrorBoundaryState
> {
  constructor(props: DashboardErrorBoundaryProps) {
    super(props)
    this.state = { 
      hasError: false,
      errorId: ''
    }
  }

  static getDerivedStateFromError(error: Error): Partial<DashboardErrorBoundaryState> {
    // Generate unique error ID for tracking
    const errorId = `dashboard-error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    return { hasError: true, error, errorId }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Dashboard Error:', {
      error,
      errorInfo,
      errorId: this.state.errorId,
      timestamp: new Date().toISOString()
    })
    
    enqueueSnackbar('Ein unerwarteter Fehler ist aufgetreten', {
      variant: 'error',
      persist: true
    })
  }

  handleReload = () => {
    window.location.reload()
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorId: '' })
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: LOADING_HEIGHT,
            gap: 2,
            p: 3,
            textAlign: 'center'
          }}
          role="alert"
          aria-live="assertive"
        >
          <Typography variant="h6" color="error" component="h1">
            Fehler beim Laden des Dashboards
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ein unerwarteter Fehler ist aufgetreten. Versuchen Sie es erneut oder laden Sie die Seite neu.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button 
              variant="outlined" 
              onClick={this.handleRetry}
              startIcon={<Refresh />}
            >
              Erneut versuchen
            </Button>
            <Button 
              variant="contained" 
              onClick={this.handleReload}
              startIcon={<Refresh />}
            >
              Seite neu laden
            </Button>
          </Box>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Typography 
              variant="caption" 
              color="text.disabled" 
              sx={{ mt: 2, fontFamily: 'monospace' }}
            >
              Error ID: {this.state.errorId}
            </Typography>
          )}
        </Box>
      )
    }

    return this.props.children
  }
}

export default DashboardErrorBoundary